import {
  orderTypeLabels,
  PurchaseOrderOrderTypeEnum,
} from "@/features/purchase-orders";
import { useOutletApi } from "@/features/authentication";
import { StatusEnum } from "@/features/sales-requests";
import { formatDate, formatNumber } from "@/features/shared";
import { CustomAsyncAutocomplete, CustomTable } from "@/features/ui";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

export const SelectLabSalesRequestSection = () => {
  const { control } = useFormContext();
  const [salesRequest, setSalesRequest] = useState(null);
  const [salesRequestProductVariants, setSalesRequestProductVariants] =
    useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);

  let addOn = null;

  const note =
    salesRequestProductVariants[0]?.purchaseOrderProductVariant?.note;
  if (note) {
    const parsed = JSON.parse(note);
    addOn = parsed.addOn || null;
  }

  const { append } = useFieldArray({
    control,
    name: "salesOrderLensProducts",
  });

  const [buyer, seller, salesOrderLensProducts] = useWatch({
    control,
    name: ["buyer", "seller", "salesOrderLensProducts"],
    exact: true,
  });

  const outletApi = useOutletApi(seller?.id);

  const disableBrowseSalesRequest = !buyer;

  const fetchUnfinishedSalesRequest = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!buyer || !seller) return [];

      const unfinishedStatuses = [
        StatusEnum.PENDING,
        StatusEnum.PARTIALLY_ORDERED,
      ].join(",");

      const excludedReferenceNumbers = salesOrderLensProducts.map(
        (item) => item.salesRequest?.referenceNumber
      );
      const {
        data: { data },
      } = await outletApi.get("sales-requests", {
        params: {
          status: unfinishedStatuses,
          orderType: PurchaseOrderOrderTypeEnum.LAB,
          withPurchaseOrder: true,
          buyerId: buyer?.id,
          keyword: searchValue || null,
          excludeReferenceNumbers:
            excludedReferenceNumbers.length > 0
              ? excludedReferenceNumbers
              : undefined,
          page,
          size,
        },
      });
      return data;
    },
    [buyer, salesOrderLensProducts, seller, outletApi]
  );

  const fetchSalesRequestProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !seller) return;

      setSalesRequestProductVariants([]);
      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `sales-requests/${referenceNumber}/sales-request-product-variants`,
          {
            params: {
              excludeFullyOrdered: true,
            },
          }
        );

        // 🔥 Adjust remaining quantity based on SOPV
        // const adjustedProductVariants = data
        //   .map((salesRequestProductVariant) => {
        //     console.log(salesRequestProductVariant);

        //     // Find if this variant already exists in SOPV
        //     const existingSalesOrderProductVariants =
        //       salesOrderProductVariants.find(
        //         (salesOrderProductVariant) =>
        //           salesOrderProductVariant.salesRequestProductVariantId ===
        //           salesRequestProductVariant.id
        //       );

        //     console.log(existingSalesOrderProductVariants);

        //     return {
        //       ...salesRequestProductVariant,
        //       adjustedRemainingQuantity:
        //         salesRequestProductVariant.purchaseOrderProductVariant
        //           .quantity -
        //         // salesRequestProductVariant.orderedQuantity -
        //         (existingSalesOrderProductVariants
        //           ? existingSalesOrderProductVariants.quantity
        //           : 0), // Adjust based on SOPV
        //     };
        //   })
        //   .filter((variant) => variant.adjustedRemainingQuantity > 0); // 🚀 Keep only variants with available stock

        setSalesRequestProductVariants(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [seller, outletApi]
  );

  useEffect(() => {
    if (salesRequest) {
      fetchSalesRequestProductVariants(salesRequest.referenceNumber);
    } else {
      setSalesRequestProductVariants([]);
    }
  }, [fetchSalesRequestProductVariants, salesRequest]);

  const submitSelection = () => {
    // Reset the state after submission
    setSalesRequest(null);
    setSalesRequestProductVariants([]);
    append({
      id: ulid(),
      price: salesRequest.purchaseOrder.grandTotal,
      salesRequest,
      salesRequestProductVariants,
    });
  };

  const columns = [
    {
      field: "purchaseOrderProductVariant.side",
      headerName: "Sisi",
    },
    { field: "purchaseOrderProductVariant.sku", headerName: "SKU" },
    {
      field: "axis",
      headerName: "Axis",
      renderCell: ({ purchaseOrderProductVariant: { note } }) => {
        try {
          const parsed = JSON.parse(note || "{}");
          return parsed.axis ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      field: "pd",
      headerName: "PD",
      renderCell: ({ purchaseOrderProductVariant: { note } }) => {
        try {
          const parsed = JSON.parse(note || "{}");
          return parsed.pd ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      field: "prism",
      headerName: "Prism",
      renderCell: ({ purchaseOrderProductVariant: { note } }) => {
        try {
          const parsed = JSON.parse(note || "{}");
          return parsed.prism ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      field: "price",
      headerName: "@Harga",
      align: "right",
      renderCell: ({ purchaseOrderProductVariant: { costPerUnit } }) =>
        formatNumber(costPerUnit),
    },
  ];

  return (
    <>
      <CustomAsyncAutocomplete
        value={salesRequest}
        onChange={setSalesRequest}
        disabled={disableBrowseSalesRequest}
        fetchOptions={fetchUnfinishedSalesRequest}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const {
            id,
            referenceNumber,
            purchaseOrder: { transactionDate, orderType },
          } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>
                  {referenceNumber} ({orderTypeLabels[orderType]})
                </div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseSalesRequest
            ? "Pilih Pelanggan terlebih dahulu"
            : undefined,
          InputProps: {
            placeholder: "Cari nomor referensi Permintaan Penjualan",
          },
        }}
      />
      {loadingProductVariants && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 1
          }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Loading...
          </Typography>
        </Box>
      )}
      {salesRequest && salesRequestProductVariants.length > 0 && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2
            }}>
            <Typography variant="body1">
              {
                salesRequestProductVariants[0].purchaseOrderProductVariant
                  .product.name
              }
            </Typography>
            {addOn && (
              <Typography variant="body2" color="primary" sx={{
                fontWeight: "500"
              }}>
                Add On: {addOn}
              </Typography>
            )}
          </Box>
          <CustomTable columns={columns} rows={salesRequestProductVariants} />
          <Typography variant="body2">
            <span style={{ color: "gray" }}>Note: </span>
            {salesRequest.purchaseOrder.note ?? "-"}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 1 }}
            onClick={submitSelection}
          >
            Tambah
          </Button>
        </Stack>
      )}
    </>
  );
};
